import {Component, forwardRef, inject, Input, OnChanges, signal, WritableSignal} from '@angular/core';
import {SelectorContainer} from '../../core/definitions/selector-container';
import {FieldValueService} from '../../core/field-value.service';
import {FieldMetaService} from '../../core/field-meta.service';
import {SearchSelectorService} from '../../core/search-selector.service';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {SectionsContainer} from '../../core/definitions/sections-container';
import {FieldParameters} from '../../core/definitions/field-parameters';
import {MetaField} from '../../core/definitions/meta-field';
import {SuperObjectModel} from '../../core/definitions/super-object-model';
import {OperationService} from '../../operations/operation.service';
import {BaseModel} from '../../core/definitions/base-model';
import {Reference, Selector} from '../../core/definitions/reference';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {SelectorCreationParams} from '../../core/definitions/selector-creation-params';
import {SearchObject} from '../../core/definitions/search-object';
import {FieldConditionService} from '../../core/field-condition.service';
import {SearchReferenceService} from "../../core/search-reference.service";
import {LoggerService} from "../../core/logger.service";
import {CrudService} from "../../core/crud.service";
import {TranslateService} from "@ngx-translate/core";
import {FeatureFlagsService} from "../../core/feature-flags.service";
import {MatDialog} from "@angular/material/dialog";
import {DrawerModalWrapperComponent} from "../../object-page-v2/drawer-modal-wrapper/drawer-modal-wrapper.component";
import {SearchContainerParams} from "../../core/definitions/search-container";
import {AConst} from "../../core/a-const.enum";
import {SearchHandlerService} from "../../object-search/search-handler.service";
import {ObjectStorageService} from "../../core/object-storage.service";
import {SearchResultSelectionsService} from "../../object-search/search-result-selections.service";
import {EditFieldSearchSelectorHandler} from "../../core/edit-field-search-selector-handler";
import {EditFieldSearchSelectorContainer} from "../../core/definitions/edit-field-search-selector-container";
import {isNewObjectPagePath} from "../../object-page-v2/is-new-object-page-path";

export interface SearchSelectorItem extends BaseModel {
  $$object: BaseModel;
  $$index: number;
  destroy: boolean;
}

@Component({
    selector: 'app-edit-field-search-selector-multiple',
    templateUrl: './edit-field-search-selector-multiple.component.html',
    styleUrls: ['./edit-field-search-selector-multiple.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => EditFieldSearchSelectorMultipleComponent)
        }
    ],
    standalone: false
})
export class EditFieldSearchSelectorMultipleComponent implements OnChanges, ControlValueAccessor {
  // Injections
  private readonly editFieldSearchSelectorHandler: EditFieldSearchSelectorHandler = inject(EditFieldSearchSelectorHandler);
  private readonly crud: CrudService = inject(CrudService);
  private readonly featureFlagsService: FeatureFlagsService = inject(FeatureFlagsService);
  private readonly fieldConditionService: FieldConditionService = inject(FieldConditionService);
  private readonly fieldMetaSvc: FieldMetaService = inject(FieldMetaService);
  private readonly fieldValueSvc: FieldValueService = inject(FieldValueService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly modalService: MatDialog = inject(MatDialog);
  private readonly objectStorage: ObjectStorageService = inject(ObjectStorageService);
  private readonly operationService: OperationService = inject(OperationService);
  private readonly searchHandler: SearchHandlerService = inject(SearchHandlerService);
  private readonly searchReferenceService: SearchReferenceService = inject(SearchReferenceService);
  private readonly searchResultSelectionsService: SearchResultSelectionsService = inject(SearchResultSelectionsService);
  private readonly searchSelector: SearchSelectorService = inject(SearchSelectorService);
  private readonly translate: TranslateService = inject(TranslateService);

  showSecondaryModal: WritableSignal<boolean> = signal(false);

  @Input() fieldParameters: FieldParameters;
  @Input() formControlName: string;

  sectionsContainer: SectionsContainer;
  object: SuperObjectModel;
  field: MetaField;
  parentIndex: number;
  disabledReason = '';
  selectorIsDisabled = false;
  editFieldSearchSelectorContainer: EditFieldSearchSelectorContainer;
  appendMode = true;

  reference: Reference;

  ngOnChanges() {
    this.editFieldSearchSelectorContainer = this.editFieldSearchSelectorHandler.initialize(this.fieldParameters, this.formControlName);
    this.sectionsContainer = this.fieldParameters.sectionsContainer;
    this.checkIsCopy();
    this.object = <SuperObjectModel>this.fieldParameters.object;
    this.field = this.fieldParameters.field;
    this.parentIndex = this.fieldParameters.parentIndex;
    this.fieldParameters.useMultipleSetter = true;
    this.fieldParameters.canDelete = false;

    const meta = this.fieldMetaSvc.getFieldMetaData(this.object, this.field);
    if (meta) {
      this.reference = this.searchReferenceService.getSearchReferenceFromField(meta);
      this.editFieldSearchSelectorHandler.setItemsAndDeletable(this.editFieldSearchSelectorContainer).then();
    }
  }

  async deleteItemByIndex(index: number) {
    if (!this.reference.ref_delete_disabled) {
      this.fieldValueSvc.deleteItem(this.sectionsContainer.rootObject, this.object, this.arrayField, index,
        this.sectionsContainer.formGroup, this.formControlName);
    }
    await this.editFieldSearchSelectorHandler.setItems(this.editFieldSearchSelectorContainer);
  }

  onItemDrop(event: CdkDragDrop<SearchSelectorItem[]>) {
    const arrayName = this.field.name;
    moveItemInArray(this.object[arrayName], event.previousIndex, event.currentIndex);
    this.object[arrayName].forEach((item: any, index: number) => {
      item['order_number'] = index;
    });
    this.editFieldSearchSelectorHandler.setItems(this.editFieldSearchSelectorContainer).then();
    this.sectionsContainer.formGroup.controls[this.formControlName].markAsDirty();
  }

  onFieldOutsideTheScreen() {
    // TODO: implement code here
  }

  async clickEnableSelector() {
    let selector: Selector, container: SelectorContainer, selected: SearchObject[];
    selector = this.reference.selector;
    container = <SelectorContainer>this.operationService.currentOperationContainer;
    selected = this.getSelectedValues();

    const params = {selected: selected} as SelectorCreationParams;
    // TODO: Add template group id to enableSelector call
    if (this.featureFlagsService.getFeatureFlags()?.experimental?.useNewObjectPage && isNewObjectPagePath(location.pathname)) {
      /*this.searchSelector.enableSelector(selector, container, params, {
        searchContainerCreated: () => {
        },
        selectorCallback: (selectedObj: BaseModel[]) => {
          this.searchSelector.disableSelector(container);
          this.checkDeleted(selectedObj);
          this.checkAddNewItems(selectedObj).then();
        }
      });*/
      // Uncommented this, keeping it just in case something breaks
      this.showSecondaryModal.set(true);

      // Create a function to handle modal closing that will be reused
      const handleModalClose = () => {
        this.showSecondaryModal.set(false);
        if (container) {
          container.selectorEnabled = false;
          // Reset the search container to force recreation on next click
          container.searchContainer = null;
        }
      };

      // Create or get the search container
      const setupSearchContainer = async () => {
        //if (!container.searchContainer) {
          const filters = this.getFilters(selector.filters, container.art);
          const singleSelect = selector.selector_type.indexOf('single') !== -1;
          const searchContainerParams = new SearchContainerParams();
          searchContainerParams.searchViewName = selector.view;
          searchContainerParams.filters = filters;
          searchContainerParams.defaultCheckedFilters = this.getFilters(selector.checked_filters, container.art);
          searchContainerParams.runSearch = false;
          searchContainerParams.selectedItems = selector.selected;
          searchContainerParams.used = selector.used || [];
          searchContainerParams.restrictions = selector.restrictions;
          searchContainerParams.keepSelected = true;

          const sc = await this.searchHandler.createSearchContainer(searchContainerParams);
          container.searchContainer = sc;
          sc.queryContainer.query = selector.query;
          sc.selections.singleSelect = singleSelect;
          sc.selections.selectCallback = (selectedObjects: SearchObject[]) => {
            this.selectCallback(selector, undefined, selectedObjects);
          };

          //const sc = await this.searchSelector.createSearchContainerForModalView(selector, container);
          this.searchSelector.setScSelectionCallback(selector, sc, {
            searchContainerCreated: () => {
            },
            selectorCallback: (selectedObj: any) => {
              this.showSecondaryModal.set(false);
              container.selectorEnabled = false;
              if (!this.appendMode) {
                this.checkDeleted(selectedObj);
              }
              this.editFieldSearchSelectorHandler.checkAddNewItems(this.editFieldSearchSelectorContainer, selectedObj).then();
            }
          });

        //}

        container.selectorEnabled = true;

        // Open the modal and handle its closing
        const modalRef = this.modalService.open(DrawerModalWrapperComponent, {
          data: {
            show: this.showSecondaryModal,
            selectorContainer: container
          }
        });

        // Add afterClosed subscription to reset the state when drawer is closed manually
        modalRef.afterClosed().subscribe(handleModalClose);
      };

      // Set up the search container and open the modal
      await setupSearchContainer();
    } else {
      // Original implementation (for pre-2024 object page)
      this.searchSelector.enableSelector(selector, container, params, {
        searchContainerCreated: () => {
        },
        selectorCallback: (selectedObj: BaseModel[]) => {
          this.searchSelector.disableSelector(container);
          if (!this.appendMode) {
            this.checkDeleted(selectedObj);
          }
          this.editFieldSearchSelectorHandler.checkAddNewItems(this.editFieldSearchSelectorContainer, selectedObj).then();
        }
      });
    }
  }

  openEdit(item: SearchSelectorItem) {
    this.editFieldSearchSelectorHandler.editItem(this.editFieldSearchSelectorContainer, item).then(art => {
      if (art) {
        this.fieldValueSvc.markAsDirty(this.sectionsContainer.formGroup, this.formControlName);
        this.editFieldSearchSelectorHandler.setItems(this.editFieldSearchSelectorContainer).then();
      }
    });
  }

  openCreateNew() {
    this.editFieldSearchSelectorHandler.openCreateNew(this.editFieldSearchSelectorContainer);
  }

  get isSelectorDisabled() {
    if (this.checkIsCopy()) {
      return true;
    }
    const disabledRes = this.fieldConditionService.runIfDisabled(this.fieldParameters);
    this.selectorIsDisabled = disabledRes.disabled;
    this.disabledReason = disabledRes.reason;
    return disabledRes.disabled;
  }

  get itemsSet() {
    return this.editFieldSearchSelectorContainer.itemsSet;
  }

  private checkIsCopy() {
    let res = false;
    if (this.sectionsContainer.isCopy) {
      this.selectorIsDisabled = true;
      this.disabledReason = this.translate.instant('TRANS__SEARCH_SELECTOR_MULTIPLE__COPY_DISABLED');
      res = true;
    }
    return res;
  }


  private getFilters(filterInfo, art) {
    const res = {};

    if (filterInfo) {
      filterInfo.forEach((filter) => {
        let filterVal;
        const sourceField = filter[AConst.SOURCE_FIELD];
        res[filter.field] = res[filter.field] || [];
        if (sourceField) {
          if (art) {
            filterVal = art[sourceField];
          } else {
            console.warn('Missing artifact for retrieving source field value: ' + sourceField);
          }
        } else if (filter.value) {
          filterVal = filter.value;
        } else {
          throw new Error('Missing filter value!');
        }
        res[filter.field].push(filterVal);
      });
    }
    return res;
  }

  private selectCallback(selector: Selector, fns, searchObjects: SearchObject[]) {
    if (!searchObjects) {
      fns.selectorCallback([]);
    } else {
      const getType = selector.selector_type.split(':')[0];
      switch (getType) {
        case 'select':
          fns.selectorCallback(searchObjects);
          break;
        case 'load':
          this.loadCallback(fns, this.getItemIds(searchObjects));
          break;
        case 'copy':
          this.copyCallback(fns, this.getItemIds(searchObjects));
          break;
        default:
          this.logger.warn(`Unknown selector type ${selector.selector_type}`);
      }
    }
  }

  private loadCallback(fns, objIds) {
    this.objectStorage.loadObjects(objIds).then(
      arts => {
        fns.selectorCallback(arts);
      },
      reason => {
        console.error(`Failed loading selected artifacts: ${reason.error?.message || reason.message}`);
      }
    );
  }

  private copyCallback(fns, objIds) {
    this.objectStorage.copyObjects(objIds).then(
      copies => {
        const artifactContainer: HTMLElement = document.querySelector('#scrollToThisFromCopyCallback');
        if (artifactContainer) {
          window.scrollTo({
            top: artifactContainer.offsetTop - 50,
            behavior: 'smooth'
          });
        }
        fns.selectorCallback(copies);
      },
      reason => {
        console.error('Failed copying selected artifacts: ' + reason.error?.message || reason.errorMessage);
      }
    );
  }

  private getItemIds(searchObjects: SearchObject[]) {
    const res = [];
    for (const selectedItem of searchObjects) {
      const itemId = this.searchResultSelectionsService.getItemId(selectedItem);
      if (itemId) {
        res.push(itemId);
      }
    }
    return res;
  }

  private getSelectedValues(): SearchObject[] {
    const res: SearchObject[] = [];
    const values = this.fieldValueSvc.getFieldValue(this.sectionsContainer.rootObject, this.formControlName);
    if (values) {
      for (let value of values) {
        if (!this.crud.getDestroy(value)) {
          res.push({
            artifact_id: value[this.reference.ref_prop]
          } as SearchObject);
        }
      }
    }
    return res;
  }


  private get arrayField(): MetaField {
    return this.field;
  }

  private async checkDeleted(objects: Array<BaseModel>) {
    const deletedList = [];
    this.getSelectedValues().forEach((existing, i) => {
      let existed = false;
      objects.forEach((obj) => {
        if (obj.artifact_id === existing.artifact_id) {
          existed = true;
        }
      });
      if (!existed) {
        deletedList.push(i);
      }
    });
    while (deletedList.length > 0) {
      await this.deleteItemByIndex(deletedList.pop());
    }
  }

  registerOnChange(/*fn: any*/): void {
    // N/A
  }

  registerOnTouched(/*fn: any*/): void {
    // N/A
  }

  setDisabledState(/*isDisabled: boolean*/): void {
    // N/A
  }

  writeValue(/*obj: any*/): void {
    // N/A
  }

}
